import React, { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "../utils/api";
import { useI18n } from "../contexts/I18nContext";
import AdBanner from "./AdBanner";

const AD_EVERY = 8; // 1 encart toutes les 8 cartes (= 4 rangées de 2)

const GENDER_FILTERS = [
  { value: "", label: "Tous" },
  { value: "homme", label: "Hommes" },
  { value: "femme", label: "Femmes" },
  { value: "non-binaire", label: "Non-binaire" },
];

const ORIENTATION_FILTERS = [
  { value: "", label: "Toutes" },
  { value: "hétéro", label: "Hétéro" },
  { value: "bi", label: "Bi" },
  { value: "gay", label: "Gay" },
  { value: "lesbienne", label: "Lesbienne" },
  { value: "pan", label: "Pan" },
  { value: "curieux·se", label: "Curieux·se" },
];

const STATUS_FILTERS = [
  { value: "", label: "Tous" },
  { value: "solo", label: "Solo" },
  { value: "en couple", label: "En couple" },
  { value: "couple ouvert", label: "Couple ouvert" },
  { value: "polyamoureux·se", label: "Poly" },
];

export default function Directory() {
  const navigate = useNavigate();
  const { t } = useI18n();
  const [profiles, setProfiles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [city, setCity] = useState("");
  const [searchCity, setSearchCity] = useState("");
  const [gender, setGender] = useState("");
  const [orientation, setOrientation] = useState("");
  const [status, setStatus] = useState("");
  const [expanded, setExpanded] = useState(null);
  const [showFilters, setShowFilters] = useState(false);

  const fetchProfiles = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (searchCity) params.set("city", searchCity);
      if (gender) params.set("gender", gender);
      if (orientation) params.set("orientation", orientation);
      if (status) params.set("relationship_status", status);
      const data = await api(`/api/profiles?${params}`);
      setProfiles(data.profiles || []);
    } catch (err) { console.error(err); }
    finally { setLoading(false); }
  }, [searchCity, gender, orientation, status]);

  useEffect(() => { fetchProfiles(); }, [fetchProfiles]);

  function handleSearch(e) { e.preventDefault(); setSearchCity(city); }

  return (
    <div className="space-y-6">
      <div>
        <h2 className="font-display text-2xl font-bold text-white">{t("directory_title")}</h2>
        <p className="mt-1 text-sm text-sensoria-text-dim">{t("directory_desc")}</p>
      </div>

      {/* Search */}
      <form onSubmit={handleSearch} className="flex gap-2">
        <input type="text" value={city} onChange={(e) => setCity(e.target.value)}
          placeholder={t("directory_search_placeholder")} className="sensoria-input flex-1" />
        <button type="submit" className="sensoria-btn px-5 py-2 text-sm">Go</button>
        <button type="button" onClick={() => setShowFilters(!showFilters)}
          className={`rounded-xl border px-3 py-2 text-sm transition-all ${
            showFilters ? "border-sensoria-accent bg-sensoria-accent/10 text-sensoria-accent-light" : "border-sensoria-border text-sensoria-text-dim"
          }`}>⚙</button>
      </form>

      {/* Filters */}
      {showFilters && (
        <div className="sensoria-card animate-slide-up grid gap-3 sm:grid-cols-3">
          <div>
            <label className="mb-1 block text-xs font-medium uppercase tracking-wider text-sensoria-text-dim">Genre</label>
            <select value={gender} onChange={(e) => setGender(e.target.value)} className="sensoria-input appearance-none text-sm">
              {GENDER_FILTERS.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
            </select>
          </div>
          <div>
            <label className="mb-1 block text-xs font-medium uppercase tracking-wider text-sensoria-text-dim">Orientation</label>
            <select value={orientation} onChange={(e) => setOrientation(e.target.value)} className="sensoria-input appearance-none text-sm">
              {ORIENTATION_FILTERS.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
            </select>
          </div>
          <div>
            <label className="mb-1 block text-xs font-medium uppercase tracking-wider text-sensoria-text-dim">Situation</label>
            <select value={status} onChange={(e) => setStatus(e.target.value)} className="sensoria-input appearance-none text-sm">
              {STATUS_FILTERS.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
            </select>
          </div>
        </div>
      )}

      {/* Results */}
      {loading ? (
        <div className="flex justify-center py-12">
          <div className="h-8 w-8 animate-spin rounded-full border-2 border-sensoria-accent border-t-transparent" />
        </div>
      ) : profiles.length === 0 ? (
        <div className="py-16 text-center">
          <div className="mb-3 text-4xl">👥</div>
          <p className="text-sensoria-text-dim">{t("directory_no_profiles")}</p>
        </div>
      ) : (
        <div className="space-y-4">
          {/* Encart pub en haut de l'annuaire */}
          <AdBanner variant="horizontal" showLabel />

          <div className="grid gap-4 sm:grid-cols-2">
          {profiles.map((p, i) => (
            <React.Fragment key={p.id}>
            {i > 0 && i % AD_EVERY === 0 && (
              <div className="col-span-full">
                <AdBanner variant="horizontal" />
              </div>
            )}
            <div className="sensoria-card transition-all hover:sensoria-glow">
              <button onClick={() => {
                setExpanded(expanded === p.id ? null : p.id);
                if (expanded !== p.id) {
                  api(`/api/visits/${p.id}`, { method: "POST" }).catch(() => {});
                }
              }} className="w-full text-left">
                <div className="flex items-start gap-3">
                  <div className="flex h-12 w-12 flex-shrink-0 items-center justify-center rounded-xl bg-sensoria-accent/10 text-2xl overflow-hidden">
                    {p.photo_url
                      ? <img src={p.photo_url} alt={p.display_name || p.username} className="h-full w-full object-cover" />
                      : (p.avatar_emoji || "🔮")
                    }
                  </div>
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-2">
                      <span className="font-semibold text-white truncate">{p.display_name || p.username}</span>
                      {p.is_verified && <span className="text-[10px] text-sensoria-success">✓</span>}
                      {p.age && <span className="text-xs text-sensoria-muted">{p.age} ans</span>}
                    </div>
                    <div className="flex flex-wrap items-center gap-2 text-xs text-sensoria-text-dim">
                      {p.city && <span>📍 {p.city}</span>}
                      {p.gender && <span>· {p.gender}</span>}
                      {p.orientation && <span>· {p.orientation}</span>}
                    </div>
                    {p.bio && <p className="mt-1.5 text-xs leading-relaxed text-sensoria-text-dim line-clamp-2">{p.bio}</p>}
                    <div className="mt-2 flex flex-wrap gap-1.5">
                      {p.relationship_status && (
                        <span className="rounded-full bg-rose-500/10 px-2 py-0.5 text-[10px] font-medium text-rose-300">{p.relationship_status}</span>
                      )}
                      {p.experience_level && (
                        <span className="rounded-full bg-sensoria-accent/10 px-2 py-0.5 text-[10px] font-medium text-sensoria-accent-light">{p.experience_level}</span>
                      )}
                      {(p.looking_for || []).slice(0, 2).map((l) => (
                        <span key={l} className="rounded-full bg-sensoria-surface px-2 py-0.5 text-[10px] text-sensoria-text-dim">{l}</span>
                      ))}
                    </div>
                  </div>
                </div>
              </button>

              {expanded === p.id && (
                <div className="mt-4 space-y-3 border-t border-sensoria-border/30 pt-4 animate-fade-in">
                  {p.bio && <p className="text-sm leading-relaxed text-sensoria-text">{p.bio}</p>}
                  {(p.looking_for || []).length > 0 && (
                    <div>
                      <span className="text-xs font-medium text-sensoria-text-dim">Recherche :</span>
                      <div className="mt-1 flex flex-wrap gap-1.5">
                        {p.looking_for.map((l) => (
                          <span key={l} className="rounded-full bg-rose-500/10 px-2.5 py-1 text-xs text-rose-300">{l}</span>
                        ))}
                      </div>
                    </div>
                  )}
                  {(p.interests || []).length > 0 && (
                    <div>
                      <span className="text-xs font-medium text-sensoria-text-dim">Intérêts :</span>
                      <div className="mt-1 flex flex-wrap gap-1.5">
                        {p.interests.map((i) => (
                          <span key={i} className="rounded-full bg-sensoria-accent/10 px-2.5 py-1 text-xs text-sensoria-accent-light">{i}</span>
                        ))}
                      </div>
                    </div>
                  )}
                  <button onClick={() => navigate(`/messages/${p.id}`)}
                    className="text-sm font-medium text-sensoria-accent hover:text-sensoria-accent-light transition-colors">
                    {t("directory_send_message")}
                  </button>
                </div>
              )}
            </div>
            </React.Fragment>
          ))}
          </div>
        </div>
      )}
    </div>
  );
}
